import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";

const DEPTS = [
  { name: "Finance", status: "Online", load: 72 },
  { name: "Marketing", status: "Active", load: 64 },
  { name: "Operations", status: "Running", load: 81 },
  { name: "HR", status: "Monitoring", load: 42 },
  { name: "Support", status: "Responding", load: 57 },
  { name: "Sales", status: "Closing Deals", load: 88 },
];

export default function HoloDashboard() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [12, 2]), {
    stiffness: 60,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), {
    stiffness: 60,
    damping: 16,
  });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative"
      style={{ perspective: 1400 }}
      data-testid="holo-dashboard-wrapper"
    >
      <div className="absolute -inset-10 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(216,192,138,0.14),transparent_65%)] blur-2xl" />
      <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full border border-dashed border-gold/20 animate-spin-slower" />
      <div className="absolute -bottom-10 -left-10 h-56 w-56 rounded-full border border-gold/10 animate-spin-slower-reverse" />

      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.3, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="glass-panel relative overflow-hidden rounded-2xl border-gold/15 shadow-[0_40px_120px_-30px_rgba(216,192,138,0.25)]"
        data-testid="holo-dashboard"
      >
        <div className="scanline" />
        <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-gold/50" />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
            Alkyrvis OS — Live Workforce
          </p>
          <div className="flex items-center gap-2">
            <span className="status-dot" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-mint">
              Nominal
            </span>
          </div>
        </div>

        <div className="space-y-1 px-4 py-4">
          {DEPTS.map((d, i) => (
            <motion.div
              key={d.name}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2 + i * 0.12, duration: 0.7 }}
              className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors duration-300 hover:bg-white/[0.04]"
              data-testid={`holo-dept-${d.name.toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                <span className="status-dot" />
                <span className="text-sm font-medium text-[#F5F5F5]">
                  {d.name}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden h-1 w-20 overflow-hidden rounded-full bg-white/5 sm:block">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-bright"
                    animate={{ width: [`${d.load - 10}%`, `${d.load}%`] }}
                    transition={{
                      duration: 2.2 + i * 0.35,
                      repeat: Infinity,
                      repeatType: "mirror",
                      ease: "easeInOut",
                    }}
                  />
                </div>
                <span className="w-24 text-right font-mono text-[10px] uppercase tracking-widest text-gold/70">
                  {d.status}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-white/5 px-6 py-3.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]">
            13 Agents Deployed
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint">
            24 / 7 / 365
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.9, duration: 0.8 }}
        className="glass-panel animate-float absolute -left-4 top-10 rounded-full border-mint/20 px-4 py-2 sm:-left-8"
        style={{ transform: "translateZ(60px)" }}
      >
        <p className="font-mono text-[10px] uppercase tracking-widest text-mint">
          Revenue +24.6%
        </p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.1, duration: 0.8 }}
        className="glass-panel animate-float-delayed absolute -bottom-6 -right-3 rounded-full border-gold/25 px-4 py-2 sm:-right-6"
      >
        <p className="font-mono text-[10px] uppercase tracking-widest text-gold">
          Uptime 99.99%
        </p>
      </motion.div>
    </div>
  );
}
